// ==========================================
// src/pages/QuemSomos.jsx — COMPLETO COM NÚMEROS CORRIGIDOS
// ==========================================
import React, { useEffect, useRef } from 'react';
import { 
  Truck, 
  Users, 
  Target, 
  Heart, 
  Shield, 
  Award,
  Phone,
  MapPin,
  CheckCircle,
  FileText,
  Search,
  Package,
  Star,
  Zap,
  TrendingUp,
  Clock,
  Globe
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { motion, useInView } from 'framer-motion';

// ==========================================
// 🌐 COMPONENTE: REDE NEURAL ANIMADA
// ==========================================
const NetworkBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    const particleCount = 60;
    const connectionDistance = 150;
    const mouseDistance = 200;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.3
      });
    }
    
    let mouse = { x: null, y: null };
    
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    
    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };
    
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;
        
        if (mouse.x != null && mouse.y != null) {
          const dx = mouse.x - particle.x;
          const dy = mouse.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < mouseDistance) {
            const force = (mouseDistance - distance) / mouseDistance;
            particle.vx += (dx / distance) * force * 0.02;
            particle.vy += (dy / distance) * force * 0.02;
          }
        }
        
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity})`;
        ctx.fill();
        
        for (let j = i + 1; j < particles.length; j++) {
          const other = particles[j];
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.3;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-auto"
      style={{ opacity: 0.6 }}
    />
  );
};

// ==========================================
// COMPONENTE: CONTADOR ANIMADO
// ==========================================
function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = React.useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, target]);
  
  return <span ref={ref}>{count.toLocaleString('pt-BR')}{suffix}</span>;
}

export default function QuemSomos() {
  const etapasOperacional = [
    {
      numero: 1,
      titulo: 'Cadastro',
      descricao: 'Motorista ou empresa faz o cadastro completo com documentos',
      icon: FileText,
      cor: 'bg-blue-500'
    },
    {
      numero: 2,
      titulo: 'Aprovação',
      descricao: 'Nossa equipe analisa e aprova o cadastro em até 24h',
      icon: CheckCircle,
      cor: 'bg-green-500'
    },
    {
      numero: 3,
      titulo: 'Busca de Cargas',
      descricao: 'Acesse cargas disponíveis e use o simulador de frete',
      icon: Search,
      cor: 'bg-purple-500'
    },
    {
      numero: 4,
      titulo: 'Fecha Frete',
      descricao: 'Clique na carga desejada e inicie o processo de contratação',
      icon: Target,
      cor: 'bg-orange-500'
    },
    {
      numero: 5,
      titulo: 'Pagamento',
      descricao: 'Realize o pagamento da taxa de agenciamento',
      icon: Zap,
      cor: 'bg-indigo-500'
    },
    {
      numero: 6,
      titulo: 'Contrato',
      descricao: 'Contrato digital liberado após confirmação do pagamento',
      icon: FileText,
      cor: 'bg-yellow-500'
    },
    {
      numero: 7,
      titulo: 'Coleta',
      descricao: 'Retire a carga no local e horário combinados',
      icon: Package,
      cor: 'bg-teal-500'
    },
    {
      numero: 8,
      titulo: 'Entrega',
      descricao: 'Entregue no destino e confirme a conclusão',
      icon: Truck,
      cor: 'bg-cyan-500'
    }
  ];

  const valores = [
    {
      icon: Shield,
      titulo: 'Segurança',
      descricao: 'Todos os cadastros são verificados para garantir operações seguras'
    },
    {
      icon: Heart,
      titulo: 'Parceria',
      descricao: 'Valorizamos cada motorista e empresa como parceiros de negócio'
    },
    {
      icon: Target,
      titulo: 'Eficiência',
      descricao: 'Tecnologia para otimizar rotas e conectar cargas rapidamente'
    },
    {
      icon: Award,
      titulo: 'Qualidade',
      descricao: 'Compromisso com a excelência em cada frete realizado'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HERO COM REDE NEURAL */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-700 to-brand-800 overflow-hidden">
        <NetworkBackground />
        
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-900/50 to-brand-900/80 pointer-events-none" />
        
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <motion.div 
            className="absolute top-20 left-20 w-72 h-72 bg-brand-500 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"
            animate={{ scale: [1, 1.3, 1], y: [0, -30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Globe className="w-4 h-4 text-brand-500" />
              <span className="text-white/90 text-sm">Tecnologia que move o Brasil</span>
            </motion.div>

            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Quem <span className="text-brand-500">Somos</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-white/80 mb-8 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Conectamos embarcadores e motoristas em todo o Brasil, 
              facilitando o transporte de cargas com segurança, eficiência e tecnologia de ponta.
            </motion.p>

            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                <div className="text-2xl font-bold text-white">
                  <AnimatedCounter target={27} />
                </div>
                <div className="text-xs text-white/70">Estados Atendidos</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                <div className="text-2xl font-bold text-white">
                  <AnimatedCounter target={24} />h
                </div>
                <div className="text-xs text-white/70">Aprovação Rápida</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                <div className="text-2xl font-bold text-white">100%</div>
                <div className="text-xs text-white/70">Digital</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* HISTÓRIA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-brand-100 text-brand-700 px-4 py-2 rounded-full mb-6">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Desde 2024</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-6">
                Nossa História
              </h2>
              
              <div className="space-y-4 text-slate-600">
                <p>
                  O <strong className="text-brand-700">BoraFrete</strong> nasceu da necessidade de modernizar o setor de transportes no Brasil. 
                  Vimos motoristas rodando vazios e empresas com dificuldade de encontrar fretes confiáveis.
                </p>
                <p>
                  Com sede em <strong>Mato Grosso</strong>, conhecemos de perto a realidade do transporte rodoviário brasileiro. 
                  Nossa plataforma foi desenvolvida para simplificar a conexão entre quem precisa transportar 
                  e quem tem capacidade de levar.
                </p>
                <p>
                  Hoje, somos referência em fretes para todo o Brasil, com foco em transparência, 
                  preços justos e parcerias duradouras.
                </p>
              </div>

              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                      {i}K+
                    </div>
                  ))}
                </div>
                <div className="text-sm text-slate-600">
                  <span className="font-bold text-brand-700">+10 mil</span> motoristas ativos
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-purple-500/20 rounded-3xl transform rotate-3" />
              <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
                <div className="grid grid-cols-2 gap-6">
                  <motion.div 
                    className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6 text-center"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="text-4xl font-bold text-brand-600 mb-2">
                      <AnimatedCounter target={1000} suffix="+" />
                    </div>
                    <div className="text-slate-600 text-sm">Cargas Publicadas</div>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="text-4xl font-bold text-green-600 mb-2">
                      <AnimatedCounter target={10000} suffix="+" />
                    </div>
                    <div className="text-slate-600 text-sm">Motoristas Ativos</div>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      <AnimatedCounter target={2000} suffix="+" />
                    </div>
                    <div className="text-slate-600 text-sm">Embarcadores</div>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="text-4xl font-bold text-orange-600 mb-2">
                      R$ <AnimatedCounter target={9} suffix=" M+" />
                    </div>
                    <div className="text-slate-600 text-sm">em Fretes/Mês</div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MISSÃO, VISÃO, VALORES */}
      <section className="py-20 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-200/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-4">
              Nossos Valores
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Princípios que guiam cada decisão e cada conexão que fazemos
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {valores.map((valor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="text-center hover:shadow-2xl transition-all duration-300 h-full border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                  <CardContent className="pt-8 pb-6">
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <valor.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-brand-900 mb-2">{valor.titulo}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{valor.descricao}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-4">
              Como Funciona
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Fluxo simplificado: clique na carga, feche o frete, pague e receba o contrato
            </p>
          </motion.div>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full w-1/3 bg-white/30"
                  animate={{ x: ['-100%', '400%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
              </div>
              
              <div className="grid grid-cols-8 gap-4">
                {etapasOperacional.map((etapa, index) => (
                  <motion.div 
                    key={index} 
                    className="relative flex flex-col items-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <motion.div 
                      className={`w-14 h-14 ${etapa.cor} rounded-full flex items-center justify-center text-white shadow-lg z-10 mb-4`}
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <etapa.icon className="w-7 h-7" />
                    </motion.div>
                    
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-brand-900 rounded-full flex items-center justify-center text-white text-xs font-bold z-20 border-2 border-white">
                      {etapa.numero}
                    </div>
                    
                    <div className="text-center mt-4">
                      <h3 className="font-bold text-brand-900 text-sm mb-1">{etapa.titulo}</h3>
                      <p className="text-slate-500 text-xs leading-tight">{etapa.descricao}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:hidden space-y-6 max-w-md mx-auto">
            {etapasOperacional.map((etapa, index) => (
              <motion.div 
                key={index} 
                className="flex items-start gap-4"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex flex-col items-center">
                  <motion.div 
                    className={`w-14 h-14 ${etapa.cor} rounded-full flex items-center justify-center text-white shadow-lg relative`}
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <etapa.icon className="w-7 h-7" />
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-brand-900 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                      {etapa.numero}
                    </div>
                  </motion.div>
                  {index < etapasOperacional.length - 1 && (
                    <motion.div 
                      className="w-0.5 h-20 bg-gradient-to-b from-slate-300 to-slate-200 mt-2"
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: false }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    />
                  )}
                </div>
                
                <div className="flex-1 pb-4 bg-slate-50 rounded-xl p-4">
                  <h3 className="font-bold text-brand-900 mb-1 text-lg">{etapa.titulo}</h3>
                  <p className="text-slate-600 text-sm">{etapa.descricao}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EQUIPE */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-4">
              Nossa Equipe
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Profissionais dedicados ao sucesso dos nossos parceiros
            </p>
          </motion.div>

          <motion.div 
            className="max-w-md mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <Card className="text-center hover:shadow-2xl transition-all duration-300 overflow-hidden border-0 shadow-xl">
              <div className="h-24 bg-gradient-to-r from-brand-600 to-brand-800" />
              <CardContent className="pt-0 pb-8 relative">
                <motion.div 
                  className="w-32 h-32 rounded-full overflow-hidden mx-auto -mt-16 mb-4 border-4 border-white shadow-xl bg-white"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img 
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop" 
                    alt="Sandra Almeida - BoraFrete"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                <h3 className="text-2xl font-bold text-brand-900 mb-1">Sandra Almeida</h3>
                <p className="text-brand-600 font-medium mb-3">BoraFrete Operacional</p>
                <p className="text-slate-500 text-sm mb-4">Gestão Operacional</p>
                
                <div className="bg-slate-50 rounded-xl p-4 text-left">
                  <p className="text-slate-600 text-sm">
                    <strong className="text-brand-700">Responsável pela aprovação de cadastros</strong> e suporte aos parceiros. 
                    Garantindo que cada motorista e empresa tenha uma experiência segura e eficiente na plataforma.
                  </p>
                </div>

                <div className="mt-4 flex justify-center gap-2">
                  <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    Online agora
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CONTATO */}
      <section className="relative py-20 bg-brand-900 overflow-hidden">
        <NetworkBackground />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-900/80 to-brand-800/80 pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Entre em Contato</h2>
            <p className="text-white/70 mb-12 max-w-2xl mx-auto">
              Estamos prontos para ajudar você a encontrar as melhores oportunidades de frete
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Phone, titulo: 'WhatsApp', valor: '(65) 98145-1096', cor: 'bg-green-500' },
              { icon: Phone, titulo: 'Telefone Fixo', valor: '(65) 3358-9139', cor: 'bg-blue-500' },
              { icon: MapPin, titulo: 'Localização', valor: 'Cuiabá, MT - Brasil', cor: 'bg-red-500' }
            ].map((contato, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className={`w-14 h-14 ${contato.cor} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <contato.icon className="w-7 h-7 text-white" />
                </div>
                <p className="text-white/60 text-sm mb-1">{contato.titulo}</p>
                <p className="text-white font-bold text-lg">{contato.valor}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RODAPÉ */}
      <section className="relative py-8 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <motion.div 
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: false }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <motion.div 
                className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <Truck className="w-5 h-5 text-white" />
              </motion.div>
              <span className="text-white font-bold text-lg">
                Bora<span className="text-brand-500">Frete</span>
              </span>
            </div>
            
            <p className="text-slate-400 text-sm text-center">
              © 2026 BoraFrete - Tecnologia que move o Brasil
            </p>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs bg-slate-800 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Sistema Online
              </div>
              <div className="flex items-center gap-2 text-slate-400 text-xs bg-slate-800 px-3 py-1 rounded-full">
                <Shield className="w-3 h-3" />
                SSL Seguro
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}